import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import ReadOnlyItem from './components/ReadOnlyItem';
import ReadOnlyPartItem from './components/ReadOnlyPartItem';
import { IItemsArray, ReadOnlyOrderDetailProps } from './interface';

import { CustomItem, ExtraItem, OrderItem } from '@config/api/task/interface';
import useIsAllUnavailableItemsCheck from '@hooks/useIsAllUnavailableItemsCheck';
import { EItemStatus } from '@utils/enums';

const ReadOnlyOrderDetail = ({
  partsLoading = false,
  truckStockItems,
  truckStockLoading = false,
  showAddedItems = false,
  items,
  customItems,
  addedItems,
  areCustomItemsLoading = false,
  isAddedItemsLoading = false,
  isFromShareableOrder = false,
  isCanceledOrder,
}: ReadOnlyOrderDetailProps) => {
  const { isAllUnavailable } =
    useIsAllUnavailableItemsCheck(isFromShareableOrder);
  const allItemsUnavailable =
    isAllUnavailable &&
    addedItems.every((item) => item.status === EItemStatus.NotAvailable);

  const isNoItemsFlow = allItemsUnavailable && !isCanceledOrder;

  //  Getting available items
  const availableItems = useMemo(() => {
    return items?.filter((item) => item?.status === EItemStatus.Available);
  }, [items]);

  const availableTruckStockItems = useMemo(() => {
    return truckStockItems?.filter(
      (item) => item?.status === EItemStatus.Available,
    );
  }, [truckStockItems]);

  const availableCustomItems = useMemo(() => {
    return customItems?.filter(
      (item) => item?.status === EItemStatus.Available,
    );
  }, [customItems]);

  const availableAddedItems = useMemo(() => {
    return addedItems?.filter((item) => item?.status === EItemStatus.Available);
  }, [addedItems]);

  const getItemsToRender = useCallback(
    (
      allListsUnavailable: boolean,
      elements: IItemsArray,
      availableElements: IItemsArray,
    ) => {
      if (isCanceledOrder) return elements || [];

      return allListsUnavailable ? elements : availableElements;
    },
    [isCanceledOrder],
  );

  const renderPartItemsElements = () => {
    if (partsLoading) return <ReadOnlyPartItem.Skeleton />;

    const filteredList = getItemsToRender(
      allItemsUnavailable,
      items || [],
      availableItems || [],
    );

    if (!filteredList || filteredList?.length === 0) return null;

    return filteredList.map((item, index) => (
      <ReadOnlyPartItem
        item={item as OrderItem}
        key={`readonly-part-item-${item.id}`}
        isLastItem={filteredList.length - 1 === index}
        noItemsFlow={isNoItemsFlow}
        isCanceledOrder={isCanceledOrder}
      />
    ));
  };

  const renderGenericElements = (
    loading = false,
    list: ExtraItem[] | CustomItem[] = [],
    availableList: ExtraItem[] | CustomItem[] = [],
  ) => {
    if (loading) return <ReadOnlyItem.Skeleton />;

    const filteredList = getItemsToRender(
      allItemsUnavailable,
      list,
      availableList,
    ) as ExtraItem[] | CustomItem[];
    if (!filteredList || filteredList?.length === 0) return null;

    return filteredList.map((item, index) => {
      return (
        <ReadOnlyItem
          name={item?.item?.info?.name}
          quantity={item?.quantity}
          key={index}
          isLastItem={filteredList.length - 1 === index}
          noItemsFlow={isNoItemsFlow}
        />
      );
    });
  };

  const renderListSection = (
    title: string,
    elements: React.ReactNode,
    isFirstListSection = false,
    subtitle?: string,
  ): React.ReactNode => {
    if (!elements) return null;

    return (
      <div
        className={classNames(
          'flex w-full flex-col items-start justify-center',
          {
            'mt-4 md:mt-2': isFirstListSection,
            'mt-7': !isFirstListSection,
          },
        )}
      >
        {subtitle ? (
          <>
            <div className='mb-2 pl-4 text-xl font-medium tracking-[.009em] text-opacity-[87%]'>
              {title}
            </div>
            <div className='pl-4 text-sm font-bold text-opacity-[87%]'>
              {subtitle}
            </div>
          </>
        ) : (
          <div className='mb-2 pl-4 text-sm font-bold text-opacity-[87%] md:mb-0'>
            {title}
          </div>
        )}
        <div className='mt-1 flex w-full flex-col items-start justify-center'>
          {elements}
        </div>
      </div>
    );
  };

  return (
    <div className='align-start mb-4 flex w-full flex-col md:justify-center'>
      <div className='pl-4 text-xl font-medium leading-8 text-black text-opacity-[87%]'>
        Initial Request
      </div>
      <div className='align-center flex w-full flex-row justify-start border-b bg-[var(--bluon--light--gray-order-detail)] px-4 py-2'>
        <div className='text-sm font-medium leading-6 text-opacity-[87%]'>
          Item
        </div>
        <div className='ml-auto text-sm font-medium leading-6 text-opacity-[87%]'>
          Qty
        </div>
      </div>
      {renderListSection('Functional Parts', renderPartItemsElements(), true)}
      {renderListSection(
        'Truck Stock',
        renderGenericElements(
          truckStockLoading,
          truckStockItems,
          availableTruckStockItems,
        ),
      )}
      {renderListSection(
        'Custom Items',
        renderGenericElements(
          areCustomItemsLoading,
          customItems,
          availableCustomItems,
        ),
      )}
      {showAddedItems &&
        renderListSection(
          'Added Items',
          renderGenericElements(
            isAddedItemsLoading,
            addedItems,
            availableAddedItems,
          ),
          false,
          'Truck Stock',
        )}
    </div>
  );
};

export default ReadOnlyOrderDetail;
